import { type TranslationKey } from '@icebreaker/shared/i18n';
import {
  Avatar,
  Button,
  EmptyState,
  ErrorBox,
  Modal,
  Skeleton,
  Table,
  Tag,
  tableStyles,
} from '@icebreaker/ui';
import { useTranslation } from '@/shared/i18n/i18n-provider';
import { useAppDispatch, useAppSelector } from '@/shared/store/hooks';
import type { RootState } from '@/shared/store/store';
import { attendeeLayerClosed, fetchEventAttendees } from '../store/admin-stats.slice';
import styles from './admin.module.css';

export const ATTENDEE_PAGE_SIZE = 25;

export interface EventAttendeesDialogProps {
  /**
   * Sunucudan başlık gelene kadar gösterilecek ad (tablodaki satırın başlığı).
   * Katmanın AÇIK olup olmadığı buradan gelmez — tek kaynak depodur.
   */
  readonly fallbackTitle: string;
}

/**
 * KATILIMCI DÖKÜMÜ KATMANI — "etkinliğe kimler katılıyor?" (kullanıcı kararı §3).
 *
 * Referans YOKTUR (pano devir paketinde çizilmemiş); katman panelin kendi
 * `UserDetailDialog` desenini izler: kicker + başlık + kişi satırları + denetim
 * notu. Esc/odak sözleşmesi kitin `Modal`ından gelir, yeniden yazılmaz.
 *
 * İNSAN ÖĞESİ (README 4.6): her satırda avatar/monogram — isimden sabit karma,
 * aynı kişi her ekranda aynı rengi taşır.
 *
 * KIRMIZI ÇİZGİ NOTU: liste yönetim içindir; katılımcının katılımcı listesinde
 * görünmesi ayrı ve kendi seçimidir. Görünmemeyi seçmiş kişi satırda etiketle
 * işaretlenir — yönetici neyi gördüğünü bilir.
 *
 * AÇIK/KAPALI KARARI TEK KAYNAKTAN: katman `adminStats.attendees.eventId` ile açılır
 * ve `attendeeLayerClosed` ile kapanır. Ekranda ikinci bir "açık mı" bayrağı
 * TUTULMAZ — iki kaynak olduğunda kapanış yalnız birini sıfırlıyor ve katman
 * boş bir tabloyla ekranda kalıyordu (bu turda ölçüldü).
 */
export function EventAttendeesDialog({ fallbackTitle }: EventAttendeesDialogProps) {
  const t = useTranslation();
  const dispatch = useAppDispatch();
  const layer = useAppSelector((state: RootState) => state.adminStats.attendees);
  const eventId = layer.eventId;

  if (eventId === null) return null;

  const title = layer.eventTitle || fallbackTitle;
  const pageCount = Math.max(1, Math.ceil(layer.total / ATTENDEE_PAGE_SIZE));
  const loadingFirst = layer.phase === 'loading' && layer.items.length === 0;

  const goToPage = (page: number): void => {
    dispatch(
      fetchEventAttendees.request({ eventId, query: { page, pageSize: ATTENDEE_PAGE_SIZE } }),
    );
  };

  return (
    <Modal
      open
      kicker={t.t('admin.overview.attendees.kicker')}
      title={title}
      closeLabel={t.t('common.close')}
      wide
      onClose={() => dispatch(attendeeLayerClosed())}
    >
      <ErrorBox error={layer.error} className={styles.errorBox}>
        <Button variant="secondary" size="sm" onClick={() => goToPage(layer.page)}>
          {t.t('common.retry')}
        </Button>
      </ErrorBox>

      {loadingFirst ? (
        <Skeleton label={t.t('a11y.loadingContent')} variant="people" lines={5} />
      ) : layer.items.length === 0 && layer.phase === 'succeeded' ? (
        <EmptyState
          title={t.t('admin.overview.attendees.emptyTitle')}
          body={t.t('admin.overview.attendees.emptyBody')}
        />
      ) : (
        <>
          <p className={styles.attendeeSummary}>
            {t.t('admin.overview.attendees.summary', {
              total: t.formatNumber(layer.total),
              checkedIn: t.formatNumber(layer.checkedInTotal),
            })}
          </p>
          <Table>
            <thead>
              <tr>
                <th>{t.t('admin.overview.attendees.table.person')}</th>
                <th>{t.t('admin.overview.attendees.table.role')}</th>
                <th>{t.t('admin.overview.attendees.table.status')}</th>
                <th>{t.t('admin.overview.attendees.table.registered')}</th>
                <th>{t.t('admin.overview.attendees.table.checkedIn')}</th>
              </tr>
            </thead>
            <tbody>
              {layer.items.map((row) => (
                <tr key={row.userId}>
                  <td>
                    <span className={styles.personCell}>
                      <Avatar name={row.displayName} size="xs" />
                      <span style={{ minWidth: 0 }}>
                        <span className={styles.personName}>{row.displayName}</span>
                        <span className={styles.personMail}>{row.email}</span>
                      </span>
                    </span>
                  </td>
                  <td className={tableStyles.cellMuted}>
                    {t.t(`admin.overview.attendees.roles.${row.role}` as TranslationKey)}
                  </td>
                  <td>
                    <Tag variant="outline">
                      {t.t(`admin.overview.attendees.statuses.${row.status}` as TranslationKey)}
                    </Tag>
                  </td>
                  <td className={styles.cellMeta}>
                    {t.formatDate(row.registeredAt, { dateStyle: 'medium' })}
                  </td>
                  <td className={styles.cellMeta}>
                    {row.checkedInAt
                      ? t.formatDate(row.checkedInAt, { dateStyle: 'short', timeStyle: 'short' })
                      : t.t('admin.overview.attendees.notCheckedIn')}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>

          {pageCount > 1 ? (
            <div className={styles.pager}>
              <Button
                variant="secondary"
                size="sm"
                disabled={layer.page <= 1}
                onClick={() => goToPage(layer.page - 1)}
              >
                {t.t('admin.overview.prevPage')}
              </Button>
              <span className={styles.pagerStatus} role="status">
                {t.t('admin.overview.pageStatus', {
                  page: t.formatNumber(layer.page),
                  pageCount: t.formatNumber(pageCount),
                })}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={!layer.hasNext}
                onClick={() => goToPage(layer.page + 1)}
              >
                {t.t('admin.overview.nextPage')}
              </Button>
            </div>
          ) : null}
        </>
      )}

      <div className={styles.auditNote}>{t.t('admin.overview.attendees.auditNote')}</div>
    </Modal>
  );
}
